import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useContentToken,
  useSlotGroup,
  EditableCollectionBinding,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import iconStudio from '../assets/floorplan-icons/studio.png';
import iconOne from '../assets/floorplan-icons/one.png';
import iconTwo from '../assets/floorplan-icons/two.png';
import dotPattern from '../assets/floorplan-icons/dot-pattern.png';

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_available_units?: string | null;
}

interface BedroomCategory {
  key: string;
  label: string;
  sublabel?: string;
  href: string;
  iconSrc: string;
}

const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];

const ICON_BY_KEY: Record<string, string> = {
  studio: iconStudio,
  one: iconOne,
  two: iconTwo,
  three: iconTwo,
  four: iconTwo,
  five: iconTwo,
};

// View All CTA follows theme primary so property brand colors flow through.
const CTA_BG = 'var(--vibe-color-primary)';
const CTA_TEXT = 'var(--vibe-color-text-on-primary)';

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View All',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  const names = ['studio', 'one', 'two', 'three', 'four', 'five'];
  return names[num] || `${num}`;
}

function keyToLabel(key: string): { label: string; sublabel?: string } {
  const labels: Record<string, { label: string; sublabel?: string }> = {
    studio: { label: 'Studio', sublabel: '(Open 1 Bed)' },
    one: { label: '1 Bed' },
    two: { label: '2 Bed' },
    three: { label: '3 Bed' },
    four: { label: '4 Bed' },
    five: { label: '5 Bed' },
  };
  return labels[key] || { label: `${key} Bed` };
}

function groupFloorplansByBeds(floorplans: Floorplan[]): BedroomCategory[] {
  const groups: Record<string, Floorplan[]> = {};

  for (const plan of floorplans) {
    const key = bedCountToKey(plan.floorplan_beds_min);
    if (!groups[key]) groups[key] = [];
    groups[key].push(plan);
  }

  const sorted = Object.keys(groups).sort((a, b) => {
    const ai = BED_ORDER.indexOf(a);
    const bi = BED_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return sorted.map((key) => {
    const { label, sublabel } = keyToLabel(key);
    return {
      key,
      label,
      sublabel,
      href: `/floor-plans?bedroom=${key}`,
      iconSrc: ICON_BY_KEY[key] || iconTwo,
    };
  });
}

function CategoryLink({ cat }: { cat: BedroomCategory }) {
  return (
    <a
      data-reveal
      href={cat.href}
      className="reveal-fade-up fp-category group relative flex w-1/2 lg:w-1/4 flex-col items-center justify-start gap-5 px-3 pt-4 pb-3 sm:pt-5 sm:pb-4 text-center"
    >
      <VibesitesImage
        url={cat.iconSrc}
        alt=""
        width={101}
        height={101}
        sizes="104px"
        className="fp-icon w-[5.5rem] h-[5.5rem] sm:w-24 sm:h-24 md:w-[6.5rem] md:h-[6.5rem] object-contain transition-transform duration-300 ease-out group-hover:scale-105"
      />
      <span className="flex min-h-[2.75em] flex-col items-center justify-start gap-0.5 font-body text-[11px] sm:text-xs md:text-[13px] font-medium uppercase tracking-[0.2em] text-black">
        <span>{cat.label}</span>
        {cat.sublabel ? <span className="text-black/70">{cat.sublabel}</span> : null}
      </span>
    </a>
  );
}

export function FloorplansList() {
  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });

  const categories = useMemo(
    () => (floorplans?.length ? groupFloorplansByBeds(floorplans) : []),
    [floorplans],
  );

  const heading = useContentToken('floorplanslist.heading');
  const cta = useSlotGroup('floorplanslist.cta_link', ctaLinkDef);

  useRevealStyles();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 100 });

  const hasCategories = categories.length > 0;

  return (
    <section className="fp-section relative overflow-hidden pt-8 md:pt-12 pb-8 md:pb-10 bg-[var(--vibe-color-background)]">
      <div
        className="fp-section-pattern pointer-events-none absolute inset-0"
        style={{ backgroundImage: `url(${dotPattern})` }}
        aria-hidden="true"
      />

      <div className="relative z-10 max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-3 md:mb-4">
          <EditableToken
            path="floorplanslist.heading"
            as="h2"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-[0.04em] text-primary mb-4"
          >
            {heading || 'Floor Plans'}
          </EditableToken>
          <RichTextContent
            path="floorplanslist.subtitle"
            as="div"
            className="font-body text-sm sm:text-base md:text-lg text-[var(--vibe-color-text-muted)] max-w-3xl mx-auto leading-snug [&_p]:m-0"
          />
        </div>

        {hasCategories ? (
          <EditableCollectionBinding bindingPath="floorplanslist.items" label="Floor Plans">
            <div
              ref={gridRef}
              className="fp-category-grid flex flex-wrap justify-center"
            >
              {categories.map((cat) => (
                <CategoryLink key={cat.key} cat={cat} />
              ))}
            </div>
          </EditableCollectionBinding>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">Floor plans will be displayed here.</p>
          </div>
        )}

        <div className="mt-2 md:mt-3 text-center">
          <EditableSlotGroup groupPath="floorplanslist.cta_link" definition={ctaLinkDef}>
            <a
              href={cta.url || '/floor-plans'}
              target={cta._target || '_self'}
              className="fp-cta inline-flex items-center justify-center min-w-[10.5rem] px-10 py-3.5 font-heading text-sm sm:text-base font-normal uppercase tracking-[0.18em]"
              style={{ backgroundColor: CTA_BG, color: CTA_TEXT }}
            >
              {cta.label || 'View All'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .fp-cta {
          transition: background 0.25s ease, color 0.25s ease;
        }
        .fp-cta:hover {
          background: #b6b6b6 !important;
          color: #000 !important;
        }
        .fp-section-pattern {
          background-repeat: repeat;
          background-size: 180px 180px;
          opacity: 0.45;
        }
        .fp-category-grid > .fp-category {
          border-bottom: 1px solid color-mix(in srgb, var(--vibe-color-border) 90%, transparent);
        }
        .fp-category-grid > .fp-category:nth-child(odd) {
          border-right: 1px solid color-mix(in srgb, var(--vibe-color-border) 90%, transparent);
        }
        @media (min-width: 1024px) {
          .fp-category-grid > .fp-category {
            border-bottom: 0;
            border-right: 1px solid color-mix(in srgb, var(--vibe-color-border) 90%, transparent);
          }
          .fp-category-grid > .fp-category:last-child,
          .fp-category-grid > .fp-category:nth-child(4n) {
            border-right: 0;
          }
        }
        @media (max-width: 1023px) {
          .fp-category-grid > .fp-category:last-child,
          .fp-category-grid > .fp-category:nth-last-child(2):nth-child(odd) {
            border-bottom: 0;
          }
          .fp-category-grid > .fp-category:nth-child(even),
          .fp-category-grid > .fp-category:last-child {
            border-right: 0;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.heading': {
      label: 'Heading',
      type: 'text',
      defaultValue: 'Floor Plans',
    },
    'floorplanslist.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultField: 'property_floorplans_blurb',
      defaultValue:
        '<p>Studio, 1 and 2-Bedroom Luxury High-Rise Apartment Homes located in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span>.</p>',
    },
  },
  slotGroups: {
    'floorplanslist.cta_link': {
      label: 'CTA Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/floor-plans',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'View All',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
  },
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(FloorplansList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FloorplansList;
